/**
 * Enterprise Authentication Context
 * Управляет состоянием аутентификации на уровне приложения.
 *
 * КРИТИЧНО:
 * - Источником правды о "входе" является именно этот контекст (isAuthenticated, user), а не Redux.
 * - Любой внешний код, который меняет токены напрямую (например, legacy Redux‑slice authSlice),
 *   обязан после успешного логина/регистрации вызвать refreshAuth(), чтобы:
 *     1) AuthContext перечитал токены из SecureStorage;
 *     2) ProtectedRoute и остальные потребители увидели актуальное isAuthenticated=true.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import { SecureStorage } from "../utils/secure-storage";
import {
  authApi,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  UserRole,
  UserRolesResponse,
} from "../services/api/auth.api";
import { languageApi } from "../services/api/language.api";
import { InternalRole } from "../types/enums/internal-roles.enum";
import { EventRole } from "../types/enums/event-roles.enum";
import { ApiError } from "../services/api-error";

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";

/**
 * Explicit lifecycle of the auth stack initialization.
 *
 * Transitions:
 *   idle → loading-user → loading-roles → ready
 *
 * ProtectedRoute MUST NOT make access decisions until status === 'ready'.
 * Any other status means the auth stack has not finished determining who the user is.
 */
export type AuthStatus =
  | "idle" // Before the first token check — no API call has started yet
  | "loading-user" // GET /auth/me (or token refresh) in flight
  | "loading-roles" // GET /auth/user/roles in flight
  | "ready"; // Fully settled — isAuthenticated reflects ground truth

export interface AuthState {
  user: AuthResponse["user"] | null;
  accessToken: string | null; // in-memory only (never localStorage)
  isAuthenticated: boolean;
  status: AuthStatus;
  error: string | null;
  userRoles: UserRolesResponse | null;
  currentRole: UserRole | null;
  availableRoles: UserRole[];
  permissions: string[];
  /**
   * FIX-11 (П0.1): заполняется, когда /auth/login возвращает requiresMfa: true.
   * Пока это состояние не очищено через verifyMfaChallenge/cancelMfaChallenge,
   * пользователь НЕ аутентифицирован — токены ещё не выпущены.
   */
  mfaChallenge: { pendingToken: string } | null;
}

export interface AuthContextType extends AuthState {
  /**
   * Backward-compat alias for `status !== 'ready'`.
   * Use `status` directly for auth-gate decisions.
   */
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  /** FIX-11 (П0.1): подтвердить MFA-код и завершить login, начатый login() с requiresMfa: true */
  verifyMfaChallenge: (code: string) => Promise<void>;
  /** Отменить текущий MFA-челлендж (например, по кнопке "Отмена" в модалке) */
  cancelMfaChallenge: () => void;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshAuth: () => Promise<void>;
  /** MFA-управление (ADR-015): начать настройку — вернуть QR и backup-коды */
  setupMfa: () => Promise<{ qrCode: string; recoveryCodes: string[] }>;
  /** Подтвердить первый TOTP-код и активировать MFA */
  confirmMfa: (code: string) => Promise<void>;
  /** Отключить MFA (серверный DELETE — код не требуется) */
  disableMfa: () => Promise<void>;
  getSessions: () => Promise<any[]>;
  terminateSession: (sessionId: string) => Promise<void>;
  logoutAll: () => Promise<void>;
  loadUserRoles: () => Promise<void>;
  switchRole: (roleId: string, eventId?: string) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: InternalRole | EventRole) => boolean;
  isInternalRole: (role: InternalRole) => boolean;
  isEventRole: (role: EventRole) => boolean;
}

const REFRESH_BEFORE_EXPIRY_MS = 60_000; // обновлять за 1 минуту до истечения

function parseJwtExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function useTokenAutoRefresh(
  accessToken: string | null,
  onRefresh: () => Promise<void>,
  onExpired: () => void,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!accessToken) return;

    const expiry = parseJwtExpiry(accessToken);
    if (!expiry) return;

    const msUntilRefresh = expiry - Date.now() - REFRESH_BEFORE_EXPIRY_MS;

    if (msUntilRefresh <= 0) {
      onRefresh().catch(onExpired);
      return;
    }

    timerRef.current = setTimeout(() => {
      onRefresh().catch(onExpired);
    }, msUntilRefresh);

    return () => clearTimeout(timerRef.current);
  }, [accessToken, onRefresh, onExpired]);
}

const CLEARED_STATE: AuthState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  status: "ready",
  error: null,
  userRoles: null,
  currentRole: null,
  availableRoles: [],
  permissions: [],
  mfaChallenge: null,
};

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isAuthenticated: false,
    status: "idle",
    error: null,
    userRoles: null,
    currentRole: null,
    availableRoles: [],
    permissions: [],
    mfaChallenge: null,
  });

  const isLoadingUserRef = useRef(false);

  // Загружаем языковые настройки пользователя при инициализации
  useEffect(() => {
    const loadLanguagePreference = async () => {
      if (!state.isAuthenticated || !state.user) {
        const savedLanguage = localStorage.getItem("selectedLanguage");
        if (savedLanguage) {
          // Здесь можно добавить логику загрузки языка, если нужно
        }
        return;
      }

      try {
        const response = await languageApi.getLanguagePreference();
        if (response.success && response.data) {
          const { language: _language } = response.data;
          // Здесь можно добавить логику переключения языка, если нужно
        }
      } catch (err) {
        console.warn("⚠️ Failed to load language preference from server:", err);
      }
    };

    loadLanguagePreference();
  }, [state.isAuthenticated, state.user]);

  const saveTokens = useCallback(async (accessToken: string) => {
    await SecureStorage.set(ACCESS_TOKEN_KEY, accessToken);
    // refreshToken не сохраняется в JS — браузер управляет HttpOnly cookie автоматически
    setState((prev) => ({ ...prev, accessToken }));
  }, []);

  const clearTokens = useCallback(async () => {
    await SecureStorage.remove(ACCESS_TOKEN_KEY);
    await SecureStorage.remove(REFRESH_TOKEN_KEY); // очищает hasRefreshToken флаг
    setState((prev) => ({ ...prev, accessToken: null }));
  }, []);

  const getAccessToken = useCallback(async () => {
    return SecureStorage.get(ACCESS_TOKEN_KEY);
  }, []);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const resolveSelectedRole = (
    rolesData: UserRolesResponse,
  ): UserRole | null => {
    const current: UserRole | null = rolesData.currentRole ?? null;
    if (current) return current;
    if (!rolesData.availableRoles?.length) return null;
    return (
      rolesData.availableRoles.find((r) => r.type === "internal") ??
      rolesData.availableRoles[0] ??
      null
    );
  };

  // Spec 22.4: guard against backend field name mismatches (name vs role, eventTitle vs eventName)
  const normalizeRole = (raw: unknown): UserRole => {
    const r = raw as Record<string, unknown>;
    const base = r as unknown as UserRole;
    const type = (r["type"] ??
      (r["eventId"] ? "event" : "internal")) as UserRole["type"];
    const rawRole = (r["role"] ?? r["name"]) as string | undefined;
    // ADR-012 (defense-in-depth): канон контракта для event-ролей — lowercase;
    // сервер нормализует на границе API (auth.service.getUserRoles), клиент
    // страхуется от регресса: в БД имена UPPERCASE ('ORGANIZER'), и без
    // нормализации role-aware меню молча деградирует до USER.
    // Internal-роли не трогаем — их канон UPPERCASE ('SUPER_ADMIN').
    const role = (
      type === "event" && typeof rawRole === "string"
        ? rawRole.toLowerCase()
        : rawRole
    ) as UserRole["role"];
    return {
      ...base,
      role,
      type,
      eventName:
        ((r["eventName"] ?? r["eventTitle"]) as string | undefined) ||
        undefined,
      assignedAt:
        (r["assignedAt"] as string | undefined) ?? new Date().toISOString(),
      assignedBy: (r["assignedBy"] as string | undefined) ?? null,
      permissions: Array.isArray(r["permissions"])
        ? (r["permissions"] as string[])
        : [],
      isActive: r["isActive"] !== false,
    };
  };

  const normalizeRolesResponse = (
    rolesData: UserRolesResponse,
  ): UserRolesResponse => ({
    ...rolesData,
    internalRoles: (rolesData.internalRoles ?? []).map(normalizeRole),
    eventRoles: (rolesData.eventRoles ?? []).map(normalizeRole),
    availableRoles: (rolesData.availableRoles ?? []).map(normalizeRole),
    currentRole: rolesData.currentRole
      ? normalizeRole(rolesData.currentRole)
      : null,
  });

  // Fallback для legacy-аккаунтов, у которых нет записей в новой ролевой системе.
  // Создаёт синтетическую UserRole из устаревшего поля user.role.
  const buildSyntheticRole = (legacyRole: string): UserRole | null => {
    const eventRoleMap: Record<string, EventRole> = {
      organizer: EventRole.ORGANIZER,
      co_organizer: EventRole.CO_ORGANIZER,
      event_admin: EventRole.EVENT_ADMIN,
      participant: EventRole.PARTICIPANT,
      speaker: EventRole.SPEAKER,
      supplier: EventRole.SUPPLIER,
      executor: EventRole.EXECUTOR,
    };
    const internalRoleMap: Record<string, InternalRole> = {
      admin: InternalRole.SUPER_ADMIN,
      super_admin: InternalRole.SUPER_ADMIN,
      platform_admin: InternalRole.PLATFORM_ADMIN,
      manager: InternalRole.PLATFORM_MANAGER,
      platform_manager: InternalRole.PLATFORM_MANAGER,
      moderator: InternalRole.CONTENT_MODERATOR,
    };
    const base = {
      id: `legacy-${legacyRole}`,
      permissions: [] as string[],
      assignedAt: new Date().toISOString(),
      assignedBy: null,
      isActive: true,
    };
    if (legacyRole in internalRoleMap) {
      return {
        ...base,
        type: "internal" as const,
        role: internalRoleMap[legacyRole]!,
      };
    }
    if (legacyRole in eventRoleMap) {
      return {
        ...base,
        type: "event" as const,
        role: eventRoleMap[legacyRole]!,
      };
    }
    return null;
  };

  const applyRoles = (rolesData: UserRolesResponse): Partial<AuthState> => {
    const normalized = normalizeRolesResponse(rolesData);
    const selectedRole = resolveSelectedRole(normalized);
    return {
      userRoles: normalized,
      currentRole: selectedRole,
      availableRoles: normalized.availableRoles,
      permissions: selectedRole?.permissions ?? [],
      status: "ready" as AuthStatus,
    };
  };

  // ─── loadCurrentUser ────────────────────────────────────────────────────────

  const loadCurrentUser = useCallback(async () => {
    let accessToken = await getAccessToken();

    // Нет токена в памяти (например, перезагрузка страницы) — пробуем восстановить сессию
    // через HttpOnly cookie (отправляется браузером автоматически при withCredentials)
    if (!accessToken) {
      try {
        const refreshResponse = await authApi.refresh();
        accessToken = refreshResponse.data.accessToken;
        await SecureStorage.set(ACCESS_TOKEN_KEY, accessToken);
        setState((prev) => ({ ...prev, accessToken }));
      } catch {
        setState((prev) => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          status: "ready",
        }));
        return;
      }
    }

    if (isLoadingUserRef.current) {
      return;
    }

    if (state.isAuthenticated && state.user && state.user.id) {
      return;
    }

    isLoadingUserRef.current = true;
    setState((prev) => ({ ...prev, status: "loading-user" }));

    try {
      const response = await authApi.getMe();
      const userData =
        response?.data != null &&
        typeof response.data === "object" &&
        "id" in response.data
          ? (response.data as AuthResponse["user"])
          : null;

      if (!userData) {
        console.warn(
          "🔐 AuthContext: getMe вернул некорректные данные пользователя",
          response,
        );
        await clearTokens();
        setState((prev) => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          status: "ready",
          error: null,
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        user: userData,
        isAuthenticated: true,
        status: "loading-roles",
        error: null,
      }));

      try {
        const rolesResponse = await authApi.getUserRoles();
        const rolesApplied = applyRoles(rolesResponse.data);
        const fallbackRole =
          !rolesApplied.currentRole && userData?.role
            ? buildSyntheticRole(userData.role)
            : null;
        setState((prev) => ({
          ...prev,
          ...rolesApplied,
          ...(fallbackRole
            ? { currentRole: fallbackRole, availableRoles: [fallbackRole] }
            : {}),
        }));
      } catch (rolesError: unknown) {
        console.error(
          "🔐 AuthContext: Ошибка загрузки ролей в loadCurrentUser",
          rolesError,
        );
        setState((prev) => ({ ...prev, status: "ready" }));
      }
    } catch (error: unknown) {
      console.error("🔐 AuthContext: Ошибка загрузки пользователя", error);

      // `httpClient` отклоняет `ApiError`, у которого поля `response` нет (AD-176).
      const status = error instanceof ApiError ? error.status : undefined;

      // 429 — не сбрасываем сессию; пользователь может быть уже аутентифицирован
      if (status === 429) {
        setState((prev) => ({
          ...prev,
          status: "ready",
          error:
            "Слишком много запросов. Подождите несколько секунд и обновите страницу.",
        }));
        return;
      }

      await clearTokens();
      setState((prev) => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        status: "ready",
        error: "Сессия истекла. Необходимо войти заново.",
      }));
    } finally {
      isLoadingUserRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessToken, saveTokens, clearTokens]); // applyRoles/state.* omitted — adding them causes infinite loop

  // ─── login ──────────────────────────────────────────────────────────────────

  const login = useCallback(
    async (credentials: LoginRequest) => {
      setState((prev) => ({ ...prev, status: "loading-user", error: null }));

      try {
        const response = await authApi.login(credentials);
        const authData = response.data ?? response;

        if (!authData) {
          throw new Error("Ответ сервера не содержит данных");
        }

        // FIX-11 (П0.1): пользователь с включённой MFA получает не токены,
        // а короткоживущий "MFA pending" токен — ждём ввода TOTP-кода,
        // прежде чем выпускать полноценную сессию (см. verifyMfaChallenge).
        if (authData.requiresMfa) {
          setState((prev) => ({
            ...prev,
            mfaChallenge: { pendingToken: authData.mfaPendingToken },
            status: "ready",
            error: null,
          }));
          return;
        }

        if (!authData.accessToken) {
          console.error(
            "🔐 AuthContext: Отсутствует accessToken в ответе!",
            authData,
          );
          throw new Error("Сервер не вернул токен авторизации");
        }

        if (!authData.user) {
          console.error(
            "🔐 AuthContext: Отсутствуют данные пользователя в ответе!",
            authData,
          );
          throw new Error("Сервер не вернул данные пользователя");
        }

        // refreshToken в HttpOnly cookie (Set-Cookie от сервера) — не сохраняем в JS
        await saveTokens(authData.accessToken);

        if ((authData as any).csrfToken) {
          localStorage.setItem("csrfToken", (authData as any).csrfToken);
        }

        setState((prev) => ({
          ...prev,
          user: authData.user,
          isAuthenticated: true,
          status: "loading-roles",
          error: null,
        }));

        try {
          // Call /auth/me before getUserRoles — the backend requires this to properly
          // initialize the session context so roles are returned correctly.
          const meResponse = await authApi.getMe();
          const freshUser =
            meResponse?.data != null &&
            typeof meResponse.data === "object" &&
            "id" in meResponse.data
              ? (meResponse.data as AuthResponse["user"])
              : authData.user;

          if (freshUser?.id) {
            setState((prev) => ({ ...prev, user: freshUser }));
          }

          const rolesResponse = await authApi.getUserRoles();
          const rolesApplied = applyRoles(rolesResponse.data);
          const effectiveRole = freshUser?.role ?? authData.user?.role;
          const fallbackRole =
            !rolesApplied.currentRole && effectiveRole
              ? buildSyntheticRole(effectiveRole)
              : null;
          setState((prev) => ({
            ...prev,
            ...rolesApplied,
            ...(fallbackRole
              ? { currentRole: fallbackRole, availableRoles: [fallbackRole] }
              : {}),
          }));
        } catch (rolesError: unknown) {
          console.error(
            "🔐 AuthContext: Ошибка загрузки ролей после входа",
            rolesError,
          );
          setState((prev) => ({ ...prev, status: "ready" }));
        }
      } catch (error: unknown) {
        console.error("🔐 AuthContext: Ошибка входа", error);

        let message = "Ошибка входа в систему";

        if (error instanceof ApiError) {
          message = error.message;
          console.error("🔐 AuthContext: ApiError:", {
            message: error.message,
            code: error.code,
            status: error.status,
            details: error.details,
          });
        } else if (error instanceof Error) {
          message = error.message;
          console.error("🔐 AuthContext: Error:", {
            message: error.message,
            name: error.name,
            stack: error.stack,
          });
        } else {
          console.error("🔐 AuthContext: Неизвестный тип ошибки:", error);
        }

        setState((prev) => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          status: "ready",
          error: message,
        }));
        throw error;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [saveTokens],
  ); // applyRoles omitted — circular dep risk in auth flow

  // ─── verifyMfaChallenge / cancelMfaChallenge ───────────────────────────────
  // FIX-11 (П0.1): завершает login начатый через login(), когда тот вернул
  // requiresMfa: true. Шлём TOTP/backup-код вместе с pending-токеном
  // (Authorization override — см. authApi.verifyMfaChallenge), получаем
  // полноценные токены с mfaVerified: true и догружаем профиль/роли через
  // тот же loadCurrentUser(), который используется при восстановлении сессии.

  const verifyMfaChallenge = useCallback(
    async (code: string) => {
      const pendingToken = state.mfaChallenge?.pendingToken;
      if (!pendingToken) {
        throw new Error("Нет активного запроса на подтверждение MFA");
      }

      setState((prev) => ({ ...prev, status: "loading-user", error: null }));

      try {
        const response = await authApi.verifyMfaChallenge(code, pendingToken);
        const tokens = response.data ?? response;

        if (!tokens?.accessToken) {
          throw new Error("Сервер не вернул токен авторизации");
        }

        await saveTokens(tokens.accessToken);
        setState((prev) => ({ ...prev, mfaChallenge: null }));
        await loadCurrentUser();
      } catch (error: unknown) {
        const message =
          error instanceof ApiError || error instanceof Error
            ? error.message
            : "Неверный код подтверждения";

        setState((prev) => ({ ...prev, status: "ready", error: message }));
        throw error;
      }
    },
    [state.mfaChallenge, saveTokens, loadCurrentUser],
  );

  const cancelMfaChallenge = useCallback(() => {
    setState((prev) => ({
      ...prev,
      mfaChallenge: null,
      status: "ready",
      error: null,
    }));
  }, []);

  // ─── register ───────────────────────────────────────────────────────────────

  const register = useCallback(
    async (data: RegisterRequest) => {
      setState((prev) => ({ ...prev, status: "loading-user", error: null }));

      try {
        const response = await authApi.register(data);
        const authData = response.data ?? response;

        if (!authData.accessToken) {
          throw new Error(
            "Некорректный ответ сервера: отсутствует accessToken",
          );
        }

        if (!authData.user) {
          throw new Error(
            "Некорректный ответ сервера: отсутствуют данные пользователя",
          );
        }

        // refreshToken в HttpOnly cookie (Set-Cookie от сервера) — не сохраняем в JS
        await saveTokens(authData.accessToken);

        setState((prev) => ({
          ...prev,
          user: authData.user,
          isAuthenticated: true,
          status: "ready",
          error: null,
        }));
      } catch (error: unknown) {
        console.error("🔐 AuthContext: Ошибка регистрации", error);

        let message = "Ошибка регистрации";
        if (error instanceof ApiError) {
          message = error.message;
        } else if (error instanceof Error) {
          message = error.message;
        }

        setState((prev) => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          status: "ready",
          error: message,
        }));
        throw error;
      }
    },
    [saveTokens],
  );

  // ─── logout ─────────────────────────────────────────────────────────────────

  /**
   * КРИТИЧНО: BC-001 - refreshToken передается в body
   * КРИТИЧНО: Audit logging для SOC 2 compliance
   */
  const logout = useCallback(async () => {
    const userId = state.user?.id;

    try {
      await authApi.logout();
      const { logAuditEvent } = await import("../utils/audit-log");
      await logAuditEvent({
        ...(userId != null ? { userId } : {}),
        action: "auth.logout",
        resource: "session",
        status: "success",
      });
    } catch (error) {
      console.warn("🔐 AuthContext: Ошибка logout на сервере", error);
      const { logAuditEvent } = await import("../utils/audit-log");
      await logAuditEvent({
        ...(userId != null ? { userId } : {}),
        action: "auth.logout",
        resource: "session",
        status: "failure",
      });
    } finally {
      await clearTokens();
      try {
        window.localStorage.setItem("logout-event", Date.now().toString());
      } catch {
        // Игнорируем ошибки localStorage
      }
      setState({ ...CLEARED_STATE });
    }
  }, [clearTokens, state.user]);

  // ─── clearError ─────────────────────────────────────────────────────────────

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // ─── refreshAuth ────────────────────────────────────────────────────────────

  const refreshAuth = useCallback(async () => {
    await loadCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // loadCurrentUser omitted intentionally — adding it causes dep chain re-creation

  // ─── MFA management (ADR-015) ────────────────────────────────────────────────

  const setupMfa = useCallback(async () => {
    try {
      const response = await authApi.setupMfa();
      return response.data;
    } catch (error: unknown) {
      console.error("🔐 AuthContext: Ошибка начала настройки MFA", error);
      throw error;
    }
  }, []);

  const confirmMfa = useCallback(async (code: string) => {
    try {
      await authApi.confirmMfa(code);
    } catch (error: unknown) {
      console.error("🔐 AuthContext: Ошибка подтверждения MFA", error);
      throw error;
    }
  }, []);

  const disableMfa = useCallback(async () => {
    try {
      await authApi.disableMfa();
    } catch (error: unknown) {
      console.error("🔐 AuthContext: Ошибка отключения MFA", error);
      throw error;
    }
  }, []);

  // ─── Sessions ───────────────────────────────────────────────────────────────

  const getSessions = useCallback(async () => {
    try {
      const response = await authApi.getSessions();
      return response.data;
    } catch (error: unknown) {
      console.error("🔐 AuthContext: Ошибка получения сессий", error);
      throw error;
    }
  }, []);

  const terminateSession = useCallback(async (sessionId: string) => {
    try {
      await authApi.terminateSession(sessionId);
    } catch (error: unknown) {
      console.error("🔐 AuthContext: Ошибка завершения сессии", error);
      throw error;
    }
  }, []);

  const logoutAll = useCallback(async () => {
    try {
      await authApi.logoutAll();
      await clearTokens();
      setState({ ...CLEARED_STATE });
    } catch (error: unknown) {
      console.error("🔐 AuthContext: Ошибка выхода со всех устройств", error);
      throw error;
    }
  }, [clearTokens]);

  // ─── Roles ──────────────────────────────────────────────────────────────────

  const loadUserRoles = useCallback(async () => {
    if (!state.isAuthenticated) {
      return;
    }

    try {
      const response = await authApi.getUserRoles();
      const rolesData = response.data;
      // Нормализуем данные, чтобы поле role всегда было заполнено (бэкенд может слать name вместо role)
      const normalized = normalizeRolesResponse(rolesData);
      setState((prev) => {
        const selectedRole = resolveSelectedRole(normalized);
        const fallbackRole =
          !selectedRole && prev.user?.role
            ? buildSyntheticRole(prev.user.role)
            : null;
        const activeRole = selectedRole || fallbackRole;
        return {
          ...prev,
          userRoles: normalized,
          currentRole: activeRole,
          availableRoles: normalized.availableRoles,
          permissions: activeRole?.permissions ?? [],
        };
      });
    } catch (error: unknown) {
      console.error("🔐 AuthContext: Ошибка загрузки ролей", error);
      setState((prev) => ({ ...prev, error: "Ошибка загрузки ролей" }));
    }
  }, [state.isAuthenticated]);

  const switchRole = useCallback(
    async (roleId: string, eventId?: string) => {
      try {
        let response;
        if (eventId) {
          response = await authApi.switchEventRole(roleId, eventId);
        } else {
          response = await authApi.switchInternalRole(roleId);
        }

        const switchData = response.data;
        const newToken = switchData.token;
        await SecureStorage.set(ACCESS_TOKEN_KEY, newToken);

        const httpClient = (await import("../services/api/api")).default;
        httpClient.defaults.headers.common["Authorization"] =
          `Bearer ${newToken}`;

        const updatedUser = switchData.user;
        setState((prev) => ({
          ...prev,
          user: { ...prev.user, ...updatedUser },
          currentRole: updatedUser.currentRole,
          permissions: updatedUser.permissions || [],
        }));

        await loadUserRoles();
      } catch (error: unknown) {
        console.error("🔐 AuthContext: Ошибка переключения роли", error);
        setState((prev) => ({ ...prev, error: "Ошибка переключения роли" }));
      }
    },
    [loadUserRoles],
  );

  // ─── Permission / role helpers ───────────────────────────────────────────────

  /**
   * КРИТИЧНО: BC-003 - используем user.permissions вместо currentRole.permissions
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      return state.permissions.includes(permission);
    },
    [state.permissions],
  );

  const hasRole = useCallback(
    (role: InternalRole | EventRole): boolean => {
      return state.currentRole?.role === role;
    },
    [state.currentRole],
  );

  const isInternalRole = useCallback(
    (role: InternalRole): boolean => {
      return (
        state.currentRole?.type === "internal" &&
        state.currentRole?.role === role
      );
    },
    [state.currentRole],
  );

  const isEventRole = useCallback(
    (role: EventRole): boolean => {
      return (
        state.currentRole?.type === "event" && state.currentRole?.role === role
      );
    },
    [state.currentRole],
  );

  // ─── Silent Refresh ─────────────────────────────────────────────────────────

  const handleSilentRefresh = useCallback(async () => {
    try {
      // refreshToken в cookie — передаётся автоматически через withCredentials
      const response = await authApi.refresh();
      const newAccessToken = response.data.accessToken;
      await SecureStorage.set("accessToken", newAccessToken);
      setState((prev) => ({ ...prev, accessToken: newAccessToken }));
    } catch {
      await logout();
    }
  }, [logout]);

  useTokenAutoRefresh(state.accessToken, handleSilentRefresh, () => {
    void logout();
  });

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    loadCurrentUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Принудительная загрузка ролей, если пользователь аутентифицирован, но роли не загружены.
  // ВАЖНО: проверяем status === 'ready', чтобы не запускать loadUserRoles параллельно с
  // loadCurrentUser (пока status === 'loading-roles') — иначе гонка перезаписывает currentRole.
  useEffect(() => {
    if (
      state.isAuthenticated &&
      state.user &&
      !state.currentRole &&
      state.status === "ready"
    ) {
      void loadUserRoles();
    }
  }, [
    state.isAuthenticated,
    state.user,
    state.currentRole,
    state.status,
    loadUserRoles,
  ]);

  // ─── Context value ───────────────────────────────────────────────────────────

  const contextValue: AuthContextType = {
    ...state,
    isLoading: state.status !== "ready",
    login,
    verifyMfaChallenge,
    cancelMfaChallenge,
    register,
    logout,
    clearError,
    refreshAuth,
    setupMfa,
    confirmMfa,
    disableMfa,
    getSessions,
    terminateSession,
    logoutAll,
    loadUserRoles,
    switchRole,
    hasPermission,
    hasRole,
    isInternalRole,
    isEventRole,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Обратная совместимость
export const useAuthContext = useAuth;

export default AuthContext;
